import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { swatchSvg } from '../lib/patterns.js';

/**
 * The legend (PRD 7.3, and the data-viz rule that identity is never colour alone): a swatch drawn
 * exactly as the mark is — same hue slot, same confidence pattern, same border treatment — beside
 * a label. Always present when a chart carries more than one series.
 *
 * Items: { label, token, fill: 'solid'|'hatch'|'dots', border: 'solid'|'dashed', muted, value }
 */
class ChartLegend extends LitElement {
  static properties = {
    items: { type: Array },
    title: { type: String },
    interactive: { type: Boolean },
  };

  constructor() {
    super();
    this.items = [];
    this.title = '';
    this.interactive = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .title {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      margin-bottom: 6px;
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 6px 16px;
    }
    li {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 12.5px;
      color: var(--ink);
    }
    li.muted {
      opacity: 0.55;
    }
    li .value {
      color: var(--ink-muted);
      font-variant-numeric: tabular-nums;
    }
    button {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      background: none;
      border: 0;
      padding: 0;
      font: inherit;
      color: inherit;
      cursor: pointer;
    }
    svg {
      display: block;
      flex: none;
    }
  `;

  _swatch(item) {
    return unsafeSVG(
      swatchSvg({
        slotVar: item.token ?? '--type-1',
        fill: item.fill ?? 'solid',
        border: item.border ?? 'solid',
        size: 14,
      }),
    );
  }

  render() {
    return html`
      ${this.title ? html`<div class="title">${this.title}</div>` : ''}
      <ul>
        ${(this.items ?? []).map(
          (item) => html`
            <li class=${item.muted ? 'muted' : ''}>
              ${
                this.interactive
                  ? html`
                      <button
                        @click=${() =>
                          this.dispatchEvent(
                            new CustomEvent('waag:legend-toggle', {
                              detail: item,
                              bubbles: true,
                              composed: true,
                            }),
                          )}
                      >
                        ${this._swatch(item)}<span>${item.label}</span>
                      </button>
                    `
                  : html`${this._swatch(item)}<span>${item.label}</span>`
              }
              ${item.value !== undefined ? html`<span class="value">${item.value}</span>` : ''}
            </li>
          `,
        )}
      </ul>
    `;
  }
}

customElements.define('chart-legend', ChartLegend);
